// Zone Builder (migration 290): the plain arithmetic behind the screen, with no React and no network. The server decides real pickups
// (m19f_zone_for); this file only draws rings and warns about them. Rings: inner miles count, outer miles do not.
const EARTH_MILES = 3958.8; // same radius as m19f_zone_for
const toRad = (d) => (d * Math.PI) / 180, toDeg = (r) => (r * 180) / Math.PI;

export const ZONE_COLORS = ['#1f77b4', '#d95f02', '#2a9d3f', '#c2185b', '#7b4fc9', '#00838f', '#a66a00', '#5d6d7e'];
export const zoneColor = (index) => ZONE_COLORS[index % ZONE_COLORS.length];

/** The map only ever uses a public token (pk.). A secret token (sk.) in this slot counts as not configured, so it never reaches a browser call. */
export const publicMapToken = (value = process.env.REACT_APP_MAPBOX_TOKEN) => (typeof value === 'string' && value.startsWith('pk.') ? value : '');

export function parseReference(text) {
    const m = String(text || '').match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    return m ? { lat: Number(m[1]), lng: Number(m[2]) } : null;
}

export function milesBetween(a, b) {
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return EARTH_MILES * 2 * Math.asin(Math.sqrt(h));
}

const number = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

export const hasRing = (zone) => number(zone.radius_start) !== null || number(zone.radius_end) !== null;
export const ownCenter = (zone) => parseReference(zone.reference_point);
export const centerOf = (zone, company) => ownCenter(zone) || (company && Number.isFinite(company.lat) && Number.isFinite(company.lng) ? { lat: company.lat, lng: company.lng } : null);
export const ringText = (zone) => (hasRing(zone) ? `${number(zone.radius_start) ?? 0}–${number(zone.radius_end) ?? 'any'} mi` : 'ZIP codes only');

export function inRing(zone, company, point) {
    if (!hasRing(zone)) return false;
    const center = centerOf(zone, company);
    if (!center) return false;
    const miles = milesBetween(center, point), outer = number(zone.radius_end);
    return miles >= (number(zone.radius_start) ?? 0) && (outer === null || miles < outer);
}

/** A closed circle of [lng, lat] points, this many miles around a center. */
export function circleCoordinates(center, miles, steps = 72) {
    const d = miles / EARTH_MILES, lat1 = toRad(center.lat), lng1 = toRad(center.lng), points = [];
    for (let i = 0; i <= steps; i += 1) {
        const bearing = (2 * Math.PI * (i % steps)) / steps;
        const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing));
        const lng2 = lng1 + Math.atan2(Math.sin(bearing) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
        points.push([toDeg(lng2), toDeg(lat2)]);
    }
    return points;
}

/** Map rings: one polygon per zone that has both a ring with an outer edge and a center, with its color. */
export function ringFeatures(zones, company) {
    return zones.flatMap((zone, index) => {
        const center = centerOf(zone, company), outer = number(zone.radius_end), inner = number(zone.radius_start) ?? 0;
        if (!center || outer === null) return [];
        const coordinates = [circleCoordinates(center, outer)];
        if (inner > 0) coordinates.push(circleCoordinates(center, inner).reverse());
        return [{ type: 'Feature', properties: { zone_id: zone.id, name: zone.name, color: zoneColor(index) }, geometry: { type: 'Polygon', coordinates } }];
    });
}

export function parseCentroids(text) {
    const map = new Map();
    String(text || '').split('\n').forEach((line) => {
        const [zip, lat, lng] = line.trim().split(',');
        if (/^\d{5}$/.test(zip || '') && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) map.set(zip, { lat: Number(lat), lng: Number(lng) });
    });
    return map;
}

/** ZIP dots for the map: those within this many miles of a center, closest first, capped so a phone stays quick. */
export function zipsNear(centroids, center, miles, cap = 1500) {
    if (!centroids || !center) return [];
    const near = [];
    centroids.forEach((point, zip) => {
        if (Math.abs(point.lat - center.lat) * 69.2 > miles) return; // quick skip before the full formula
        const distance = milesBetween(center, point);
        if (distance <= miles) near.push({ zip, ...point, distance });
    });
    return near.sort((a, b) => a.distance - b.distance).slice(0, cap);
}

export const zoneHoldingZip = (zones, zip, exceptId = null) => zones.find((z) => z.id !== exceptId && (z.zip_codes || []).includes(zip)) || null;

/** Add typed or tapped ZIPs to a list: five digits, no repeats, and a ZIP can only belong to one zone. */
export function addZips(list, text, zones, zoneId) {
    const next = [...list], notes = [];
    String(text || '').split(/[\s,;]+/).filter(Boolean).forEach((zip) => {
        if (!/^\d{5}$/.test(zip)) { notes.push(`${zip} is not a 5-digit ZIP code.`); return; }
        if (next.includes(zip)) { notes.push(`${zip} is already on this zone's list.`); return; }
        const holder = zoneHoldingZip(zones, zip, zoneId);
        if (holder) { notes.push(`${zip} is already in ${holder.name}. Take it out of that zone first.`); return; }
        next.push(zip);
    });
    return { list: next.sort(), notes };
}

/** Inline form checks. The server repeats them. */
export function validateZone(draft) {
    const errors = {};
    if (!String(draft.name || '').trim()) errors.name = 'Enter the zone name.';
    const minutes = Number(draft.base_minutes);
    if (draft.base_minutes === '' || draft.base_minutes === null || draft.base_minutes === undefined || !Number.isInteger(minutes) || minutes < 1 || minutes > 240) errors.base_minutes = 'Drive time must be 1 to 240 whole minutes.';
    const innerBlank = draft.radius_start === '' || draft.radius_start === null || draft.radius_start === undefined;
    const outerBlank = draft.radius_end === '' || draft.radius_end === null || draft.radius_end === undefined;
    const inner = innerBlank ? 0 : Number(draft.radius_start), outer = Number(draft.radius_end);
    if (!innerBlank && (!Number.isFinite(inner) || inner < 0)) errors.radius_start = 'Inner miles cannot be negative.';
    if (outerBlank && !innerBlank) errors.radius_end = 'Enter the outer miles too, or leave both blank for a ZIP-only zone.';
    else if (!outerBlank && (!Number.isFinite(outer) || outer <= inner)) errors.radius_end = 'Outer miles must be more than inner miles.';
    else if (!outerBlank && outer > 1000) errors.radius_end = 'Outer miles must be 1,000 or less.';
    if (draft.own_center && !draft.center_address && !parseReference(draft.reference_point)) errors.center = "Pick this zone's center from the address search.";
    return errors;
}

// Same rounding as the server's "same spot" test: round(value::numeric, 6), a true decimal round with halves going away from zero.
const round6 = (value) => {
    const shifted = Number(`${Math.abs(value)}e6`);
    return Number.isFinite(shifted) ? ((Math.sign(value) * Math.round(shifted)) / 1e6).toFixed(6) : value.toFixed(6);
};
const key = (center) => (center ? `${round6(center.lat)},${round6(center.lng)}` : 'none');

const startOf = (zone) => number(zone.radius_start) ?? 0;
// The order the server tries rings in (m19f_zone_for): lower starting mile, then lower display order (none comes last), then zone id.
function ringOrder(a, b) {
    const shownA = number(a.display_order), shownB = number(b.display_order);
    if (startOf(a) !== startOf(b)) return startOf(a) - startOf(b);
    if (shownA !== shownB) return shownA === null ? 1 : shownB === null ? -1 : shownA - shownB;
    return String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0;
}
/** Says which of two overlapping rings an address in both lands in, by the server's exact order. */
function whoWins(a, b) {
    const winner = ringOrder(a, b) <= 0 ? a : b;
    if (startOf(a) !== startOf(b)) return `An address in both lands in ${winner.name}, the ring with the lower starting mile.`;
    if (number(a.display_order) !== number(b.display_order)) return `Both start at ${startOf(a)} miles, so an address in both lands in ${winner.name}, the one with the lower display order (a zone with no display order comes last).`;
    return `Both start at ${startOf(a)} miles and have the same display order, so an address in both lands in ${winner.name}, the one with the lower internal zone ID.`;
}

/** How close a ring around one center comes to a second center, and how far it reaches: the span of miles it covers, measured from that second center. */
function reachFrom(zone, ownPoint, from) {
    const d = milesBetween(from, ownPoint), inner = number(zone.radius_start) ?? 0, outer = number(zone.radius_end);
    return { near: Math.max(0, inner - d, outer === null ? 0 : d - outer), far: outer === null ? Infinity : d + outer };
}

/**
 * Warnings, never blocks: overlapping rings (including an own-center ring over the company's rings or over another own-center ring), gaps between rings, a ring with no center, a zone that can never match, and a ZIP whose center
 * sits in a different zone's ring. Each may carry a fix: { zone_id, patch, label } that opens that zone's form with the change filled in, unsaved.
 */
export function zoneWarnings(zones = [], company = null, centroids = null) {
    const out = [], groups = new Map();
    zones.forEach((zone) => {
        if (!hasRing(zone)) {
            if (!(zone.zip_codes || []).length) out.push({ id: `empty-${zone.id}`, kind: 'empty', text: `${zone.name} has no ring and no ZIP codes, so no address can land in it.`, fix: { zone_id: zone.id, patch: {}, label: `Edit ${zone.name}` } });
            return;
        }
        const center = centerOf(zone, company);
        if (!center) { out.push({ id: `center-${zone.id}`, kind: 'center', text: `${zone.name} has a ring but no center point yet, so its ring cannot match anything. Choose a center above.` }); return; }
        const group = key(center);
        if (!groups.has(group)) groups.set(group, []);
        groups.get(group).push(zone);
    });
    groups.forEach((rings) => {
        const sorted = [...rings].sort(ringOrder);
        const first = number(sorted[0].radius_start) ?? 0;
        if (first > 0) out.push({ id: `gap-start-${sorted[0].id}`, kind: 'gap', text: `Nothing covers the first ${first} miles from the center. ${sorted[0].name} starts at ${first}.`, fix: { zone_id: sorted[0].id, patch: { radius_start: 0 }, label: `Start ${sorted[0].name} at 0` } });
        let reach = null, reachZone = null;
        sorted.forEach((zone) => {
            const inner = number(zone.radius_start) ?? 0, outer = number(zone.radius_end);
            if (reachZone) {
                if (inner < reach) {
                    const fits = outer === null || reach < outer;
                    out.push({ id: `overlap-${reachZone.id}-${zone.id}`, kind: 'overlap', text: `${reachZone.name} and ${zone.name} overlap from ${inner} to ${outer === null ? reach : Math.min(reach, outer)} miles. ${whoWins(reachZone, zone)}`,
                        fix: fits ? { zone_id: zone.id, patch: { radius_start: reach }, label: `Start ${zone.name} at ${reach}` } : { zone_id: zone.id, patch: {}, label: `Edit ${zone.name}` } });
                } else if (inner > reach) {
                    out.push({ id: `gap-${reachZone.id}-${zone.id}`, kind: 'gap', text: `Gap from ${reach} to ${inner} miles between ${reachZone.name} and ${zone.name}. An address there uses the flat drive time and is flagged "check pickup time".`,
                        fix: { zone_id: reachZone.id, patch: { radius_end: inner }, label: `Stretch ${reachZone.name} to ${inner}` } });
                }
            }
            if (outer !== null && (reach === null || outer > reach)) { reach = outer; reachZone = zone; }
        });
    });
    const companyCenter = centerOf({}, company);
    if (companyCenter) {
        const companyKey = key(companyCenter), companyRings = groups.get(companyKey) || [];
        groups.forEach((rings, group) => {
            if (group === companyKey) return;
            rings.forEach((zone) => {
                const { near, far } = reachFrom(zone, centerOf(zone, company), companyCenter);
                companyRings.filter((c) => { const outer = number(c.radius_end); return (outer === null || near < outer) && far > (number(c.radius_start) ?? 0); }).forEach((c) => {
                    out.push({ id: `own-overlap-${zone.id}-${c.id}`, kind: 'own-overlap', text: `${zone.name} uses its own center, and its ring overlaps ${c.name}'s ring. ${whoWins(zone, c)}`, fix: { zone_id: zone.id, patch: {}, label: `Edit ${zone.name}` } });
                });
            });
        });
    }
    // Two rings that each have their own center, at two different spots.
    const ownGroups = [...groups.entries()].filter(([group]) => !companyCenter || group !== key(companyCenter)).map(([, rings]) => rings);
    ownGroups.forEach((rings, index) => ownGroups.slice(index + 1).forEach((others) => rings.forEach((zone) => others.forEach((other) => {
        const { near, far } = reachFrom(zone, ownCenter(zone), ownCenter(other)), outer = number(other.radius_end);
        if ((outer === null || near < outer) && far > startOf(other)) {
            out.push({ id: `own-own-overlap-${zone.id}-${other.id}`, kind: 'own-overlap', text: `${zone.name} and ${other.name} each use their own center, and their rings overlap. ${whoWins(zone, other)}`, fix: { zone_id: zone.id, patch: {}, label: `Edit ${zone.name}` } });
        }
    }))));
    if (centroids) {
        zones.forEach((holder) => (holder.zip_codes || []).forEach((zip) => {
            const point = centroids.get(zip);
            if (!point || inRing(holder, company, point)) return;
            const ring = zones.find((z) => z.id !== holder.id && inRing(z, company, point));
            if (ring) out.push({ id: `zip-${zip}`, kind: 'zip', text: `ZIP ${zip} is on ${holder.name}'s list, but its center sits inside ${ring.name}'s ring. The ZIP rule wins, so it stays ${holder.name}. Leave it if that is what you want.`,
                fix: { zone_id: holder.id, patch: { remove_zip: zip }, label: `Take ${zip} out of ${holder.name}` } });
        }));
    }
    return out;
}

/** Why an address landed where it did, in plain words. */
export function whyText(result) {
    const zone = result.zone || {}, timing = result.timing || {};
    switch (result.matched_by) {
    case 'zip': return `ZIP ${zone.zip} is on ${zone.name}'s list. A ZIP rule always beats a ring.`;
    case 'radius': return `${zone.miles} miles from the center, inside ${zone.name}'s ring.`;
    case 'beyond': return `${zone.miles} ${zone.road_miles ? 'driving' : 'straight-line'} miles, past the last ring. Uses ${String(zone.name).replace(/\+$/, '')}'s time plus ${timing.minutes_per_mile ?? 2} minutes a mile over ${timing.over_miles ?? 50} miles.`;
    case 'gap': return `${zone.miles} miles from the center, in a gap between rings. Uses the flat drive time, and dispatch sees "check pickup time".`;
    default: return result.has_location
        ? 'No ZIP rule or ring covers this address. Uses the flat drive time, and dispatch sees "Zone unknown – check pickup time".'
        : 'No ZIP rule matches, and this address has no known location, so rings cannot help. Pick it from the search list to use rings. Uses the flat drive time, and dispatch sees "Zone unknown – check pickup time".';
    }
}
